import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addEvent,
  archiveItem,
  createItem,
  createRelation,
  createTask,
  deleteAttachment,
  deleteRelation,
  errorMessage,
  exportInventory,
  getAttachmentBlob,
  getInventoryOptions,
  getInventoryOverview,
  getItem,
  getItemQrBlob,
  importInventory,
  linkTicket,
  restoreItem,
  unlinkTicket,
  updateItem,
  updateTask,
  uploadAttachment,
} from '../../utils/inventoryApi';
import styles from './Inventory.module.css';

const TYPE_ICONS = {
  pc: 'PC', monitor: '▣', printer: 'PR', online_cash: '₽', scanner: '▤',
  payment_terminal: 'PT', other_peripheral: 'IO', network: 'NET', dvr: 'DVR',
  camera: 'CAM', ups: 'UPS', phone: 'MOB', other: 'IT',
};

const SPEC_LABELS = {
  cpu: 'Процессор', ram: 'Оперативная память', storage: 'Накопители', os: 'Операционная система',
  channels: 'Количество каналов', hdd_count: 'Количество HDD', hdd_capacity: 'Общий объём HDD',
  archive_days: 'Глубина архива, дней', firmware: 'Версия прошивки', resolution: 'Разрешение',
  lens: 'Объектив', power: 'Питание / PoE', ports: 'Количество портов', poe: 'PoE',
  connection: 'Подключение', consumable: 'Расходный материал', capacity: 'Мощность', battery_date: 'Дата замены батареи',
};
const TASK_PRIORITY_LABELS = { low: 'Низкий', normal: 'Обычный', high: 'Высокий', urgent: 'Срочный' };

const EMPTY_FILTERS = {
  q: '', status: '', item_type: '', network_id: '', point_id: '',
  maintenance: '', responsible_user_id: '', needs_attention: false, archived: false,
};

const toDateInput = value => value ? String(value).slice(0, 10) : '';
const cleanPayload = payload => Object.fromEntries(
  Object.entries(payload).map(([key, value]) => [key, value === '' ? null : value]),
);

function formatDate(value, fallback = 'Не указано') {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleDateString('ru-RU');
}

function formatMoney(value) {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(value);
}

function formatSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 ** 2).toFixed(1)} МБ`;
}

function daysLabel(value) {
  if (!value) return 'ТО не запланировано';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(value);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86400000);
  if (days < 0) return `Просрочено на ${Math.abs(days)} дн.`;
  if (days === 0) return 'ТО сегодня';
  if (days === 1) return 'ТО завтра';
  return `Через ${days} дн.`;
}

function SelectOptions({ options = [], empty = 'Не выбрано' }) {
  return (
    <>
      <option value="">{empty}</option>
      {options.map(option => <option key={option.value ?? option.id} value={option.value ?? option.id}>{option.label ?? option.name}</option>)}
    </>
  );
}

function PointOptions({ networks = [] }) {
  return (
    <>
      <option value="">Без привязки к точке</option>
      {networks.map(network => (
        <optgroup key={network.id} label={network.name}>
          {(network.points || []).map(point => <option key={point.id} value={point.id}>{point.name}</option>)}
        </optgroup>
      ))}
    </>
  );
}

function StatusBadge({ status, label }) {
  return <span className={`${styles.statusBadge} ${styles[`status_${status}`] || ''}`}><i />{label || status}</span>;
}

function MaintenanceBadge({ item }) {
  return (
    <span className={`${styles.maintenanceBadge} ${styles[`maintenance_${item.maintenance_state}`] || ''}`}>
      {daysLabel(item.next_maintenance_at)}
    </span>
  );
}

function StatCard({ tone, value, label, hint, active, onClick }) {
  return (
    <button type="button" className={`${styles.statCard} ${styles[tone] || ''} ${active ? styles.statActive : ''}`} onClick={onClick}>
      <span className={styles.statValue}>{value ?? 0}</span>
      <span className={styles.statLabel}>{label}</span>
      <small>{hint}</small>
    </button>
  );
}

export default function Inventory() {
  const navigate = useNavigate();
  const initialAssetId = useMemo(() => new URLSearchParams(window.location.search).get('asset'), []);
  const [options, setOptions] = useState(null);
  const [overview, setOverview] = useState({ items: [], stats: {} });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editor, setEditor] = useState(null);
  const [selectedId, setSelectedId] = useState(initialAssetId ? Number(initialAssetId) : null);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const loadOptions = useCallback(async () => {
    setOptions(await getInventoryOptions());
  }, []);

  const loadOverview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== false));
      if (filters.archived) params.archived = true;
      setOverview(await getInventoryOverview(params));
    } catch (requestError) {
      setError(errorMessage(requestError, 'Не удалось загрузить реестр'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadOptions().catch(requestError => setError(errorMessage(requestError, 'Не удалось загрузить справочники')));
  }, [loadOptions]);

  useEffect(() => {
    const timer = window.setTimeout(() => loadOverview(), filters.q ? 280 : 0);
    return () => window.clearTimeout(timer);
  }, [filters, loadOverview]);

  const openItem = id => {
    setSelectedId(id);
    const params = new URLSearchParams(window.location.search);
    params.set('asset', id);
    navigate({ search: params.toString() }, { replace: true });
  };

  const closeItem = () => {
    setSelectedId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('asset');
    navigate({ search: params.toString() }, { replace: true });
  };

  const setFilter = (key, value) => setFilters(current => ({
    ...current,
    [key]: value,
    ...(key === 'network_id' ? { point_id: '' } : {}),
    ...(key === 'status' ? { needs_attention: false } : {}),
    ...(key === 'needs_attention' ? { status: '' } : {}),
  }));

  const filteredPoints = useMemo(() => {
    const networks = options?.networks || [];
    if (!filters.network_id) return networks.flatMap(network => network.points || []);
    return networks.find(network => String(network.id) === String(filters.network_id))?.points || [];
  }, [filters.network_id, options]);

  const afterChange = async () => {
    await Promise.all([loadOverview(true), loadOptions()]);
  };

  const handleImport = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    setError('');
    try {
      const result = await importInventory(file);
      const errorText = result.errors?.length ? ` Ошибок: ${result.errors.length}.` : '';
      setNotice(`Импорт завершён: добавлено ${result.created}, обновлено ${result.updated}.${errorText}`);
      await afterChange();
    } catch (requestError) {
      setError(errorMessage(requestError, 'Не удалось импортировать файл'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>УЧЁТ И ОБСЛУЖИВАНИЕ</div>
          <h1>Оборудование</h1>
          <p>Вся техника, её состояние, история работ и следующие действия — в одном месте.</p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={() => importRef.current?.click()} disabled={importing}>
            {importing ? 'Импортируем…' : 'Импорт Excel'}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={() => exportInventory().catch(requestError => setError(errorMessage(requestError)))}>
            Экспорт
          </button>
          <button type="button" className={styles.primaryButton} onClick={() => setEditor({ mode: 'create' })}>
            <span aria-hidden="true">＋</span> Добавить оборудование
          </button>
          <input ref={importRef} type="file" accept=".xlsx" hidden onChange={handleImport} />
        </div>
      </header>

      {error && <div className={styles.alertError}><span>!</span>{error}<button type="button" onClick={() => setError('')}>×</button></div>}
      {notice && <div className={styles.alertSuccess}><span>✓</span>{notice}<button type="button" onClick={() => setNotice('')}>×</button></div>}

      <section className={styles.statsGrid} aria-label="Сводка по оборудованию">
        <StatCard tone="statNeutral" value={overview.stats?.total} label="Всего в работе" hint="Активный реестр" active={!filters.maintenance && !filters.status && !filters.needs_attention && !filters.archived} onClick={() => setFilters(EMPTY_FILTERS)} />
        <StatCard tone="statWarning" value={overview.stats?.attention} label="Требует внимания" hint="Неисправно или в ремонте" active={filters.needs_attention} onClick={() => setFilter('needs_attention', !filters.needs_attention)} />
        <StatCard tone="statDanger" value={overview.stats?.maintenance_overdue} label="ТО просрочено" hint="Нужно запланировать работы" active={filters.maintenance === 'overdue'} onClick={() => setFilter('maintenance', filters.maintenance === 'overdue' ? '' : 'overdue')} />
        <StatCard tone="statPurple" value={overview.stats?.maintenance_soon} label="ТО в ближайшие 30 дней" hint="Предстоящие обслуживания" active={filters.maintenance === 'soon'} onClick={() => setFilter('maintenance', filters.maintenance === 'soon' ? '' : 'soon')} />
        <StatCard tone="statBlue" value={overview.stats?.open_tasks} label="Открытые задачи" hint={`${overview.stats?.overdue_tasks || 0} просрочено`} />
      </section>

      <section className={styles.registryCard}>
        <div className={styles.toolbar}>
          <label className={styles.searchBox}>
            <span aria-hidden="true">⌕</span>
            <input value={filters.q} onChange={event => setFilter('q', event.target.value)} placeholder="Название, IP, серийный или инвентарный номер…" />
            {filters.q && <button type="button" onClick={() => setFilter('q', '')} aria-label="Очистить поиск">×</button>}
          </label>
          <div className={styles.filterGrid}>
            <select value={filters.network_id} onChange={event => setFilter('network_id', event.target.value)} aria-label="Сеть">
              <option value="">Все сети</option>
              {(options?.networks || []).map(network => <option key={network.id} value={network.id}>{network.name}</option>)}
            </select>
            <select value={filters.point_id} onChange={event => setFilter('point_id', event.target.value)} aria-label="Точка">
              <option value="">Все точки</option>
              {filteredPoints.map(point => <option key={point.id} value={point.id}>{point.name}</option>)}
            </select>
            <select value={filters.item_type} onChange={event => setFilter('item_type', event.target.value)} aria-label="Тип оборудования">
              <SelectOptions options={options?.types} empty="Все типы" />
            </select>
            <select value={filters.status} onChange={event => setFilter('status', event.target.value)} aria-label="Статус">
              <SelectOptions options={options?.statuses} empty="Все статусы" />
            </select>
            <select value={filters.maintenance} onChange={event => setFilter('maintenance', event.target.value)} aria-label="Срок обслуживания">
              <option value="">Любой срок ТО</option>
              <option value="overdue">ТО просрочено</option>
              <option value="soon">ТО в ближайшие 30 дней</option>
              <option value="unscheduled">ТО не запланировано</option>
            </select>
          </div>
          <label className={styles.archiveToggle}>
            <input type="checkbox" checked={filters.archived} onChange={event => setFilter('archived', event.target.checked)} />
            <span>Архив</span>
          </label>
        </div>

        <div className={styles.resultMeta}>
          <strong>{loading ? 'Обновляем…' : `${overview.total || 0} ${overview.total === 1 ? 'единица' : 'единиц'} техники`}</strong>
          {Object.entries(filters).some(([key, value]) => key !== 'archived' && value) && (
            <button type="button" onClick={() => setFilters(current => ({ ...EMPTY_FILTERS, archived: current.archived }))}>Сбросить фильтры</button>
          )}
        </div>

        {loading ? (
          <div className={styles.loadingState}><span className="spinner" /><p>Собираем данные по оборудованию…</p></div>
        ) : overview.items?.length ? (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.assetTable}>
                <thead><tr><th>Оборудование</th><th>Точка</th><th>Идентификаторы</th><th>Состояние</th><th>Обслуживание</th><th aria-label="Действия" /></tr></thead>
                <tbody>{overview.items.map(item => <AssetRow key={item.id} item={item} onOpen={() => openItem(item.id)} />)}</tbody>
              </table>
            </div>
            <div className={styles.mobileAssetList}>
              {overview.items.map(item => <AssetCard key={item.id} item={item} onOpen={() => openItem(item.id)} />)}
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>▦</div>
            <h2>{filters.q || Object.values(filters).some(Boolean) ? 'Ничего не найдено' : 'Добавьте первую единицу техники'}</h2>
            <p>{filters.q ? 'Попробуйте изменить поиск или снять часть фильтров.' : 'Создайте понятный паспорт устройства и запланируйте обслуживание.'}</p>
            {!filters.q && <button type="button" className={styles.primaryButton} onClick={() => setEditor({ mode: 'create' })}>＋ Добавить оборудование</button>}
          </div>
        )}
      </section>

      {editor && options && (
        <AssetEditor
          initial={editor.item}
          options={options}
          onClose={() => setEditor(null)}
          onSaved={async item => { setEditor(null); await afterChange(); openItem(item.id); }}
        />
      )}
      {selectedId && options && (
        <AssetDrawer
          itemId={selectedId}
          options={options}
          onClose={closeItem}
          onEdit={item => setEditor({ mode: 'edit', item })}
          onChanged={afterChange}
          onArchived={() => { closeItem(); afterChange(); }}
        />
      )}
    </div>
  );
}

function AssetRow({ item, onOpen }) {
  return (
    <tr onClick={onOpen} tabIndex="0" onKeyDown={event => event.key === 'Enter' && onOpen()}>
      <td>
        <div className={styles.assetIdentity}>
          <span className={styles.typeIcon}>{TYPE_ICONS[item.item_type] || 'IT'}</span>
          <span><strong>{item.name}</strong><small>{item.type_label}{item.brand || item.model_name ? ` · ${[item.brand, item.model_name].filter(Boolean).join(' ')}` : ''}</small></span>
        </div>
      </td>
      <td><strong className={styles.cellPrimary}>{item.point?.name || 'Без точки'}</strong><small>{[item.point?.network_name, item.location_note].filter(Boolean).join(' · ') || 'Место не указано'}</small></td>
      <td><strong className={styles.monoText}>{item.ip_address || item.inventory_number || '—'}</strong><small>{item.serial_number ? `S/N ${item.serial_number}` : item.hostname || 'Нет дополнительных данных'}</small></td>
      <td><StatusBadge status={item.status} label={item.status_label} />{item.open_tasks_count > 0 && <small className={item.overdue_tasks_count ? styles.overdueText : ''}>{item.open_tasks_count} открыт. задач</small>}</td>
      <td><strong className={item.maintenance_state === 'overdue' ? styles.overdueText : styles.cellPrimary}>{formatDate(item.next_maintenance_at, 'Не запланировано')}</strong><small><MaintenanceBadge item={item} /></small></td>
      <td><button type="button" className={styles.rowAction} onClick={event => { event.stopPropagation(); onOpen(); }} aria-label={`Открыть ${item.name}`}>›</button></td>
    </tr>
  );
}

function AssetCard({ item, onOpen }) {
  return (
    <button type="button" className={styles.assetCard} onClick={onOpen}>
      <div className={styles.assetCardTop}>
        <span className={styles.typeIcon}>{TYPE_ICONS[item.item_type] || 'IT'}</span>
        <span className={styles.assetCardTitle}><strong>{item.name}</strong><small>{item.type_label} · {item.point?.name || 'Без точки'}</small></span>
        <span className={styles.rowAction}>›</span>
      </div>
      <div className={styles.assetCardMeta}>
        <StatusBadge status={item.status} label={item.status_label} />
        <MaintenanceBadge item={item} />
      </div>
      <div className={styles.assetCardDetails}>
        <span><small>IP / №</small><strong>{item.ip_address || item.inventory_number || '—'}</strong></span>
        <span><small>Следующее ТО</small><strong>{formatDate(item.next_maintenance_at, 'Не назначено')}</strong></span>
      </div>
    </button>
  );
}

function ModalShell({ title, subtitle, onClose, children, footer, wide = false }) {
  useEffect(() => {
    const handler = event => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className={`${styles.modal} ${wide ? styles.modalWide : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className={styles.modalHeader}><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div><button type="button" onClick={onClose} aria-label="Закрыть">×</button></header>
        <div className={styles.modalContent}>{children}</div>
        {footer && <footer className={styles.modalFooter}>{footer}</footer>}
      </section>
    </div>
  );
}

const initialAssetForm = item => ({
  item_type: item?.item_type || 'pc', name: item?.name || '', point_id: item?.point_id || '',
  location_note: item?.location_note || '', status: item?.status || 'active', criticality: item?.criticality || 'normal',
  inventory_number: item?.inventory_number || '', serial_number: item?.serial_number || '', brand: item?.brand || '',
  model_name: item?.model_name || '', ip_address: item?.ip_address || '', mac_address: item?.mac_address || '',
  hostname: item?.hostname || '', comment: item?.comment || '', installed_at: toDateInput(item?.installed_at),
  purchase_date: toDateInput(item?.purchase_date), warranty_until: toDateInput(item?.warranty_until),
  responsible_user_id: item?.responsible_user_id || '', maintenance_interval_days: item?.maintenance_interval_days || '',
  last_maintenance_at: toDateInput(item?.last_maintenance_at), next_maintenance_at: toDateInput(item?.next_maintenance_at),
  fields: item?.fields || {},
});

function FormField({ label, hint, children, className = '' }) {
  return <label className={`${styles.formField} ${className}`}><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

function AssetEditor({ initial, options, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialAssetForm(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const setSpec = (key, value) => setForm(current => ({ ...current, fields: { ...current.fields, [key]: value } }));

  const submit = async event => {
    event.preventDefault();
    if (!form.name.trim()) return setError('Введите понятное название оборудования');
    setSaving(true);
    setError('');
    try {
      const payload = cleanPayload({
        ...form,
        point_id: form.point_id ? Number(form.point_id) : null,
        responsible_user_id: form.responsible_user_id ? Number(form.responsible_user_id) : null,
        maintenance_interval_days: form.maintenance_interval_days ? Number(form.maintenance_interval_days) : null,
      });
      const item = initial ? await updateItem(initial.id, payload) : await createItem(payload);
      onSaved(item);
    } catch (requestError) {
      setError(errorMessage(requestError, 'Не удалось сохранить оборудование'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={initial ? 'Редактировать паспорт' : 'Новое оборудование'}
      subtitle="Сначала заполните главное. Технические параметры можно дополнить позже."
      onClose={onClose}
      wide
      footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Отмена</button><button type="submit" form="asset-form" className={styles.primaryButton} disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить паспорт'}</button></>}
    >
      <form id="asset-form" onSubmit={submit} className={styles.editorForm}>
        {error && <div className={styles.inlineError}>{error}</div>}
        <div className={styles.formSection}>
          <div className={styles.formSectionHeading}><span>1</span><div><h3>Что это и где находится</h3><p>Название должно быть узнаваемым без открытия карточки.</p></div></div>
          <div className={styles.formGrid}>
            <FormField label="Название *" className={styles.span2}><input autoFocus value={form.name} onChange={event => set('name', event.target.value)} placeholder="Регистратор — торговый зал" /></FormField>
            <FormField label="Тип оборудования"><select value={form.item_type} onChange={event => set('item_type', event.target.value)}><SelectOptions options={options.types} empty="Выберите тип" /></select></FormField>
            <FormField label="Статус"><select value={form.status} onChange={event => set('status', event.target.value)}><SelectOptions options={options.statuses} /></select></FormField>
            <FormField label="Точка"><select value={form.point_id} onChange={event => set('point_id', event.target.value)}><PointOptions networks={options.networks} /></select></FormField>
            <FormField label="Место внутри точки"><input value={form.location_note} onChange={event => set('location_note', event.target.value)} placeholder="Серверная, касса №2, стойка…" /></FormField>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formSectionHeading}><span>2</span><div><h3>Паспорт и сеть</h3><p>Эти данные участвуют в общем поиске.</p></div></div>
          <div className={styles.formGrid}>
            <FormField label="Производитель"><input value={form.brand} onChange={event => set('brand', event.target.value)} placeholder="Hikvision, MikroTik…" /></FormField>
            <FormField label="Модель"><input value={form.model_name} onChange={event => set('model_name', event.target.value)} /></FormField>
            <FormField label="Инвентарный номер"><input value={form.inventory_number} onChange={event => set('inventory_number', event.target.value)} placeholder="ИТ-00123" /></FormField>
            <FormField label="Серийный номер"><input value={form.serial_number} onChange={event => set('serial_number', event.target.value)} /></FormField>
            <FormField label="IP-адрес"><input value={form.ip_address} onChange={event => set('ip_address', event.target.value)} inputMode="decimal" placeholder="192.168.1.10" /></FormField>
            <FormField label="MAC-адрес"><input value={form.mac_address} onChange={event => set('mac_address', event.target.value)} placeholder="AA:BB:CC:DD:EE:FF" /></FormField>
            <FormField label="Hostname"><input value={form.hostname} onChange={event => set('hostname', event.target.value)} /></FormField>
            <FormField label="Критичность"><select value={form.criticality} onChange={event => set('criticality', event.target.value)}><SelectOptions options={options.criticalities} /></select></FormField>
          </div>
          <TechnicalFields type={form.item_type} values={form.fields} onChange={setSpec} />
        </div>

        <div className={styles.formSection}>
          <div className={styles.formSectionHeading}><span>3</span><div><h3>Обслуживание и ответственность</h3><p>Даты используются для индикаторов и Telegram-напоминаний.</p></div></div>
          <div className={styles.formGrid}>
            <FormField label="Ответственный"><select value={form.responsible_user_id} onChange={event => set('responsible_user_id', event.target.value)}><SelectOptions options={options.users} empty="Только системные администраторы" /></select></FormField>
            <FormField label="Интервал ТО, дней"><input type="number" min="1" max="3650" value={form.maintenance_interval_days} onChange={event => set('maintenance_interval_days', event.target.value)} placeholder="180" /></FormField>
            <FormField label="Последнее ТО"><input type="date" value={form.last_maintenance_at} onChange={event => set('last_maintenance_at', event.target.value)} /></FormField>
            <FormField label="Следующее ТО"><input type="date" value={form.next_maintenance_at} onChange={event => set('next_maintenance_at', event.target.value)} /></FormField>
            <FormField label="Дата установки"><input type="date" value={form.installed_at} onChange={event => set('installed_at', event.target.value)} /></FormField>
            <FormField label="Дата покупки"><input type="date" value={form.purchase_date} onChange={event => set('purchase_date', event.target.value)} /></FormField>
            <FormField label="Гарантия до"><input type="date" value={form.warranty_until} onChange={event => set('warranty_until', event.target.value)} /></FormField>
            <FormField label="Заметки" className={styles.span2}><textarea rows="3" value={form.comment} onChange={event => set('comment', event.target.value)} placeholder="Что важно знать об устройстве. Пароли здесь не храните." /></FormField>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

function TechnicalFields({ type, values = {}, onChange }) {
  const schemas = {
    pc: [['cpu', 'Процессор'], ['ram', 'Оперативная память'], ['storage', 'Накопители'], ['os', 'Операционная система']],
    dvr: [['channels', 'Количество каналов'], ['hdd_count', 'Количество HDD'], ['hdd_capacity', 'Общий объём HDD'], ['archive_days', 'Глубина архива, дней'], ['firmware', 'Версия прошивки']],
    camera: [['resolution', 'Разрешение'], ['lens', 'Объектив'], ['power', 'Питание / PoE'], ['firmware', 'Версия прошивки']],
    network: [['ports', 'Количество портов'], ['poe', 'PoE'], ['firmware', 'Версия прошивки']],
    printer: [['connection', 'Подключение'], ['consumable', 'Расходный материал']],
    ups: [['capacity', 'Мощность'], ['battery_date', 'Дата замены батареи']],
  };
  const fields = schemas[type];
  if (!fields) return null;
  return (
    <div className={styles.technicalBox}>
      <h4>Параметры для типа «{type}»</h4>
      <div className={styles.formGrid}>{fields.map(([key, label]) => <FormField key={key} label={label}><input value={values[key] || ''} onChange={event => onChange(key, event.target.value)} /></FormField>)}</div>
    </div>
  );
}

const DETAIL_TABS = [
  ['overview', 'Паспорт'], ['service', 'История'], ['tasks', 'Задачи'], ['files', 'Файлы'], ['links', 'Связи'],
];

function AssetDrawer({ itemId, options, onClose, onEdit, onChanged, onArchived }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [dialog, setDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try { setItem(await getItem(itemId)); }
    catch (requestError) { setError(errorMessage(requestError, 'Не удалось открыть карточку')); }
    finally { setLoading(false); }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    await load();
    await onChanged();
  };

  const uploadFiles = async event => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      for (const file of files) await uploadAttachment(item.id, file);
      await refresh();
    } catch (requestError) {
      setError(errorMessage(requestError, 'Не удалось загрузить файл'));
    } finally { setUploading(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Перенести «${item.name}» в архив? История и файлы сохранятся.`)) return;
    try { await archiveItem(item.id); onArchived(); }
    catch (requestError) { setError(errorMessage(requestError)); }
  };

  return (
    <div className={styles.drawerBackdrop} role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Карточка оборудования">
        <header className={styles.drawerHeader}>
          <button type="button" className={styles.drawerClose} onClick={onClose} aria-label="Закрыть карточку">×</button>
          {loading ? <div className={styles.drawerLoading}><span className="spinner" /></div> : item && (
            <>
              <div className={styles.drawerTitleRow}>
                <span className={styles.typeIconLarge}>{TYPE_ICONS[item.item_type] || 'IT'}</span>
                <div><div className={styles.drawerEyebrow}>{item.type_label} · #{item.id}</div><h2>{item.name}</h2><p>{[item.point?.network_name, item.point?.name, item.location_note].filter(Boolean).join(' · ') || 'Место не указано'}</p></div>
              </div>
              <div className={styles.drawerBadges}><StatusBadge status={item.status} label={item.status_label} /><MaintenanceBadge item={item} />{item.criticality === 'high' && <span className={styles.criticalBadge}>Критичное</span>}</div>
            </>
          )}
        </header>

        {!loading && item && <nav className={styles.detailTabs}>{DETAIL_TABS.map(([value, label]) => (
          <button key={value} type="button" className={tab === value ? styles.activeTab : ''} onClick={() => setTab(value)}>
            {label}{value === 'tasks' && item.open_tasks_count > 0 && <span>{item.open_tasks_count}</span>}{value === 'files' && item.attachments_count > 0 && <span>{item.attachments_count}</span>}
          </button>
        ))}</nav>}

        <div className={styles.drawerBody}>
          {error && <div className={styles.inlineError}>{error}</div>}
          {loading ? <div className={styles.loadingState}><span className="spinner" /><p>Открываем карточку…</p></div> : item && (
            <>
              {tab === 'overview' && <OverviewTab item={item} />}
              {tab === 'service' && <ServiceTab item={item} options={options} onAdd={() => setDialog('event')} />}
              {tab === 'tasks' && <TasksTab item={item} onAdd={() => setDialog('task')} onToggle={async task => { await updateTask(task.id, { status: task.status === 'done' ? 'open' : 'done' }); refresh(); }} />}
              {tab === 'files' && <FilesTab item={item} uploading={uploading} onUpload={() => fileRef.current?.click()} onDeleted={refresh} setError={setError} />}
              {tab === 'links' && <LinksTab item={item} onAddRelation={() => setDialog('relation')} onAddTicket={() => setDialog('ticket')} onChanged={refresh} setError={setError} />}
            </>
          )}
        </div>

        {!loading && item && <footer className={styles.drawerFooter}>
          <button type="button" className={styles.secondaryButton} onClick={() => setDialog('qr')}>QR-метка</button>
          {item.archived_at ? <button type="button" className={styles.secondaryButton} onClick={async () => { await restoreItem(item.id); refresh(); }}>Вернуть из архива</button> : <button type="button" className={styles.dangerButton} onClick={archive}>В архив</button>}
          <button type="button" className={styles.primaryButton} onClick={() => onEdit(item)}>Изменить паспорт</button>
        </footer>}
        <input ref={fileRef} type="file" multiple hidden onChange={uploadFiles} />
      </aside>

      {dialog === 'event' && <EventEditor item={item} options={options} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await refresh(); }} />}
      {dialog === 'task' && <TaskEditor item={item} options={options} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await refresh(); }} />}
      {dialog === 'relation' && <RelationEditor item={item} options={options} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await refresh(); }} />}
      {dialog === 'ticket' && <TicketEditor item={item} options={options} onClose={() => setDialog(null)} onSaved={async () => { setDialog(null); await refresh(); }} />}
      {dialog === 'qr' && <QrDialog item={item} onClose={() => setDialog(null)} />}
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  return <div className={styles.infoRow}><span>{label}</span><strong className={mono ? styles.monoText : ''}>{value || '—'}</strong></div>;
}

function OverviewTab({ item }) {
  return (
    <div className={styles.tabStack}>
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}><div><h3>Основное</h3><p>Данные для поиска и быстрого определения устройства</p></div></div>
        <div className={styles.infoGrid}>
          <InfoRow label="Точка" value={item.point?.name} />
          <InfoRow label="Место" value={item.location_note} />
          <InfoRow label="Производитель / модель" value={[item.brand, item.model_name].filter(Boolean).join(' ')} />
          <InfoRow label="Инвентарный номер" value={item.inventory_number} mono />
          <InfoRow label="Серийный номер" value={item.serial_number} mono />
          <InfoRow label="Ответственный" value={item.responsible_user?.name} />
        </div>
      </section>
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}><div><h3>Сеть и идентификация</h3></div></div>
        <div className={styles.infoGrid}><InfoRow label="IP-адрес" value={item.ip_address} mono /><InfoRow label="MAC-адрес" value={item.mac_address} mono /><InfoRow label="Hostname" value={item.hostname} mono /><InfoRow label="Критичность" value={item.criticality === 'high' ? 'Критичная' : item.criticality === 'low' ? 'Низкая' : 'Обычная'} /></div>
      </section>
      {Object.keys(item.fields || {}).some(key => item.fields[key]) && <section className={styles.detailSection}><div className={styles.sectionHeader}><div><h3>Технические параметры</h3></div></div><div className={styles.infoGrid}>{Object.entries(item.fields).filter(([, value]) => value).map(([key, value]) => <InfoRow key={key} label={SPEC_LABELS[key] || key.replaceAll('_', ' ')} value={value} />)}</div></section>}
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}><div><h3>Сроки</h3></div></div>
        <div className={styles.infoGrid}><InfoRow label="Установлено" value={formatDate(item.installed_at)} /><InfoRow label="Куплено" value={formatDate(item.purchase_date)} /><InfoRow label="Гарантия до" value={formatDate(item.warranty_until)} /><InfoRow label="Интервал ТО" value={item.maintenance_interval_days ? `${item.maintenance_interval_days} дней` : null} /><InfoRow label="Последнее ТО" value={formatDate(item.last_maintenance_at)} /><InfoRow label="Следующее ТО" value={formatDate(item.next_maintenance_at)} /></div>
      </section>
      <section className={styles.noteSection}><h3>Заметки</h3><p>{item.comment || 'Заметок пока нет.'}</p><small>Не храните здесь пароли и другие секреты.</small></section>
    </div>
  );
}

function ServiceTab({ item, options, onAdd }) {
  const eventLabels = Object.fromEntries((options.event_types || []).map(option => [option.value, option.label]));
  return (
    <div className={styles.tabStack}>
      <div className={styles.tabIntro}><div><h3>История обслуживания</h3><p>Фиксируйте выполненные работы, замены, расходы и следующую дату ТО.</p></div><button type="button" className={styles.primaryButton} onClick={onAdd}>＋ Добавить запись</button></div>
      <div className={styles.maintenanceSummary}>
        <div><small>Последнее ТО</small><strong>{formatDate(item.last_maintenance_at)}</strong></div>
        <div className={item.maintenance_state === 'overdue' ? styles.summaryDanger : ''}><small>Следующее ТО</small><strong>{formatDate(item.next_maintenance_at)}</strong><span>{daysLabel(item.next_maintenance_at)}</span></div>
      </div>
      {item.events?.length ? <div className={styles.timeline}>{item.events.map(event => (
        <article key={event.id} className={styles.timelineItem}>
          <div className={styles.timelineMarker} />
          <div className={styles.timelineCard}>
            <div className={styles.timelineTop}><span>{eventLabels[event.log_type] || event.log_type}</span><time>{formatDate(event.event_date)}</time></div>
            <p>{event.description}</p>
            <div className={styles.timelineMeta}>{event.performed_by && <span>Выполнил: <strong>{event.performed_by}</strong></span>}{event.parts && <span>Материалы: <strong>{event.parts}</strong></span>}{event.cost !== null && <span>Расходы: <strong>{formatMoney(event.cost)}</strong></span>}{event.ticket_id && <span>Заявка: <strong>#{event.ticket_id}</strong></span>}{event.next_maintenance_at && <span>Следующее ТО: <strong>{formatDate(event.next_maintenance_at)}</strong></span>}</div>
          </div>
        </article>
      ))}</div> : <SmallEmpty title="История пока пуста" text="Добавьте первую запись после осмотра, установки или обслуживания." action="Добавить запись" onAction={onAdd} />}
    </div>
  );
}

function TasksTab({ item, onAdd, onToggle }) {
  return (
    <div className={styles.tabStack}>
      <div className={styles.tabIntro}><div><h3>Что нужно сделать</h3><p>Покупки, замены и повторные проверки не потеряются в комментариях.</p></div><button type="button" className={styles.primaryButton} onClick={onAdd}>＋ Новая задача</button></div>
      {item.tasks?.length ? <div className={styles.taskList}>{item.tasks.map(task => {
        const overdue = task.due_at && new Date(task.due_at) < new Date() && !['done', 'cancelled'].includes(task.status);
        return <article key={task.id} className={`${styles.taskCard} ${task.status === 'done' ? styles.taskDone : ''}`}>
          <button type="button" className={styles.taskCheck} onClick={() => onToggle(task)} aria-label={task.status === 'done' ? 'Вернуть задачу' : 'Выполнить задачу'}>{task.status === 'done' ? '✓' : ''}</button>
          <div className={styles.taskContent}><div className={styles.taskTitle}><strong>{task.title}</strong><span className={`${styles.priorityBadge} ${styles[`priority_${task.priority}`] || ''}`}>{TASK_PRIORITY_LABELS[task.priority] || task.priority}</span></div>{task.description && <p>{task.description}</p>}<div className={styles.taskMeta}><span className={overdue ? styles.overdueText : ''}>{task.due_at ? `${overdue ? 'Просрочено' : 'Срок'}: ${formatDate(task.due_at)}` : 'Без срока'}</span>{task.assigned_user && <span>{task.assigned_user.name}</span>}{task.ticket && <span>{task.ticket.ticket_number}</span>}</div></div>
        </article>;
      })}</div> : <SmallEmpty title="Открытых действий нет" text="После ТО можно создать задачу: купить диск, заменить кабель или повторно проверить устройство." action="Создать задачу" onAction={onAdd} />}
    </div>
  );
}

function FilesTab({ item, uploading, onUpload, onDeleted, setError }) {
  return (
    <div className={styles.tabStack}>
      <div className={styles.tabIntro}><div><h3>Фотографии и документы</h3><p>Фото установки, акты, чеки, схемы и инструкции хранятся на Яндекс Диске.</p></div><button type="button" className={styles.primaryButton} onClick={onUpload} disabled={uploading}>{uploading ? 'Загружаем…' : '＋ Загрузить'}</button></div>
      {item.attachments?.length ? <div className={styles.fileGrid}>{item.attachments.map(attachment => <AttachmentCard key={attachment.id} attachment={attachment} onDeleted={onDeleted} setError={setError} />)}</div> : <SmallEmpty title="Файлов пока нет" text="Можно загрузить фотографию оборудования, PDF, таблицу или другой документ до 100 МБ." action="Загрузить файл" onAction={onUpload} />}
    </div>
  );
}

function AttachmentCard({ attachment, onDeleted, setError }) {
  const [preview, setPreview] = useState('');
  const [opening, setOpening] = useState(false);
  const isImage = attachment.content_type?.startsWith('image/');
  useEffect(() => {
    let active = true;
    let url = '';
    if (isImage) getAttachmentBlob(attachment.id).then(blob => { if (active) { url = URL.createObjectURL(blob); setPreview(url); } }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [attachment.id, isImage]);
  const open = async () => {
    setOpening(true);
    try {
      const blob = await getAttachmentBlob(attachment.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (requestError) { setError(errorMessage(requestError, 'Не удалось открыть файл')); }
    finally { setOpening(false); }
  };
  return (
    <article className={styles.fileCard}>
      <button type="button" className={styles.filePreview} onClick={open}>{preview ? <img src={preview} alt="" /> : <span>{attachment.kind === 'photo' ? 'IMG' : (attachment.name.split('.').pop() || 'FILE').toUpperCase()}</span>}</button>
      <div className={styles.fileInfo}><strong title={attachment.name}>{attachment.name}</strong><small>{formatSize(attachment.size)} · {formatDate(attachment.created_at)}</small></div>
      <div className={styles.fileActions}><button type="button" onClick={open} disabled={opening}>{opening ? 'Открываем…' : 'Открыть'}</button><button type="button" className={styles.textDanger} onClick={async () => { if (!window.confirm(`Удалить «${attachment.name}»?`)) return; try { await deleteAttachment(attachment.id); onDeleted(); } catch (requestError) { setError(errorMessage(requestError)); } }}>Удалить</button></div>
    </article>
  );
}

function LinksTab({ item, onAddRelation, onAddTicket, onChanged, setError }) {
  return (
    <div className={styles.tabStack}>
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}><div><h3>Связанное оборудование</h3><p>Камеры регистратора, периферия ПК, замена старого устройства.</p></div><button type="button" className={styles.secondaryButton} onClick={onAddRelation}>＋ Связать</button></div>
        {item.relations?.length ? <div className={styles.linkList}>{item.relations.map(relation => <div key={relation.id} className={styles.linkRow}><span className={styles.typeIcon}>{TYPE_ICONS[relation.item.item_type] || 'IT'}</span><div><strong>{relation.item.name}</strong><small>{relation.relation_label} · {relation.item.point?.name || 'Без точки'}</small></div><button type="button" onClick={async () => { try { await deleteRelation(relation.id); onChanged(); } catch (requestError) { setError(errorMessage(requestError)); } }} aria-label="Удалить связь">×</button></div>)}</div> : <p className={styles.sectionEmpty}>Связанного оборудования пока нет.</p>}
      </section>
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}><div><h3>Заявки Helpdesk</h3><p>Открывайте заявку вместе с контекстом конкретного устройства.</p></div><button type="button" className={styles.secondaryButton} onClick={onAddTicket}>＋ Связать</button></div>
        {item.tickets?.length ? <div className={styles.linkList}>{item.tickets.map(ticket => <div key={ticket.link_id} className={styles.ticketRow}><a href={`/admin/tickets/${ticket.id}`}><strong>{ticket.ticket_number}</strong><span>{ticket.description}</span><small>{ticket.point} · {ticket.status}</small></a><button type="button" onClick={async () => { try { await unlinkTicket(ticket.link_id); onChanged(); } catch (requestError) { setError(errorMessage(requestError)); } }} aria-label="Отвязать заявку">×</button></div>)}</div> : <p className={styles.sectionEmpty}>Связанных заявок пока нет.</p>}
      </section>
    </div>
  );
}

function SmallEmpty({ title, text, action, onAction }) {
  return <div className={styles.smallEmpty}><span>＋</span><h4>{title}</h4><p>{text}</p>{action && <button type="button" className={styles.secondaryButton} onClick={onAction}>{action}</button>}</div>;
}

function EventEditor({ item, options, onClose, onSaved }) {
  const [form, setForm] = useState({ log_type: 'maintenance', description: '', event_date: toDateInput(new Date().toISOString()), performed_by: '', parts: '', cost: '', next_maintenance_at: '', ticket_id: '' });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const submit = async event => {
    event.preventDefault();
    if (!form.description.trim()) return setError('Опишите, что было сделано');
    setSaving(true); setError('');
    try {
      const record = await addEvent(item.id, cleanPayload({ ...form, cost: form.cost ? Number(form.cost) : null, ticket_id: form.ticket_id ? Number(form.ticket_id) : null }));
      for (const file of files) await uploadAttachment(item.id, file, record.id);
      onSaved();
    } catch (requestError) { setError(errorMessage(requestError, 'Не удалось сохранить запись')); }
    finally { setSaving(false); }
  };
  return <ModalShell title="Запись в историю" subtitle={`Работы по «${item.name}»`} onClose={onClose} wide footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Отмена</button><button type="submit" form="event-form" className={styles.primaryButton} disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить запись'}</button></>}>
    <form id="event-form" className={styles.compactForm} onSubmit={submit}>{error && <div className={styles.inlineError}>{error}</div>}<div className={styles.formGrid}>
      <FormField label="Тип события"><select value={form.log_type} onChange={event => set('log_type', event.target.value)}><SelectOptions options={options.event_types} /></select></FormField>
      <FormField label="Дата работ"><input type="date" value={form.event_date} onChange={event => set('event_date', event.target.value)} /></FormField>
      <FormField label="Что сделано *" className={styles.span2}><textarea autoFocus rows="4" value={form.description} onChange={event => set('description', event.target.value)} placeholder="Проверил камеры, очистил регистратор, обновил прошивку…" /></FormField>
      <FormField label="Кто выполнил"><input value={form.performed_by} onChange={event => set('performed_by', event.target.value)} placeholder="Если оставить пустым — текущий администратор" /></FormField>
      <FormField label="Материалы и комплектующие"><input value={form.parts} onChange={event => set('parts', event.target.value)} placeholder="HDD 4 ТБ, кабель 5 м…" /></FormField>
      <FormField label="Расходы, ₽"><input type="number" min="0" step="0.01" value={form.cost} onChange={event => set('cost', event.target.value)} /></FormField>
      <FormField label="Следующее ТО"><input type="date" value={form.next_maintenance_at} onChange={event => set('next_maintenance_at', event.target.value)} /></FormField>
      <FormField label="Связанная заявка"><select value={form.ticket_id} onChange={event => set('ticket_id', event.target.value)}><option value="">Без заявки</option>{options.tickets.map(ticket => <option key={ticket.id} value={ticket.id}>{ticket.ticket_number} · {ticket.point}</option>)}</select></FormField>
      <FormField label="Фото и документы"><input type="file" multiple onChange={event => setFiles([...event.target.files])} /><small>{files.length ? `Выбрано файлов: ${files.length}` : 'Можно приложить акты и фотографии.'}</small></FormField>
    </div></form>
  </ModalShell>;
}

function TaskEditor({ item, options, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', priority: 'normal', due_at: '', assigned_user_id: '', ticket_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const submit = async event => {
    event.preventDefault();
    if (!form.title.trim()) return setError('Введите задачу');
    setSaving(true); setError('');
    try { await createTask(item.id, cleanPayload({ ...form, assigned_user_id: form.assigned_user_id ? Number(form.assigned_user_id) : null, ticket_id: form.ticket_id ? Number(form.ticket_id) : null })); onSaved(); }
    catch (requestError) { setError(errorMessage(requestError, 'Не удалось создать задачу')); }
    finally { setSaving(false); }
  };
  return <ModalShell title="Новая задача" subtitle={`Следующее действие по «${item.name}»`} onClose={onClose} footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Отмена</button><button type="submit" form="task-form" className={styles.primaryButton} disabled={saving}>{saving ? 'Создаём…' : 'Создать задачу'}</button></>}>
    <form id="task-form" className={styles.compactForm} onSubmit={submit}>{error && <div className={styles.inlineError}>{error}</div>}<div className={styles.formGrid}>
      <FormField label="Что нужно сделать *" className={styles.span2}><input autoFocus value={form.title} onChange={event => set('title', event.target.value)} placeholder="Купить HDD 4 ТБ" /></FormField>
      <FormField label="Подробности" className={styles.span2}><textarea rows="3" value={form.description} onChange={event => set('description', event.target.value)} /></FormField>
      <FormField label="Приоритет"><select value={form.priority} onChange={event => set('priority', event.target.value)}><SelectOptions options={options.task_priorities} /></select></FormField>
      <FormField label="Срок"><input type="date" value={form.due_at} onChange={event => set('due_at', event.target.value)} /></FormField>
      <FormField label="Исполнитель"><select value={form.assigned_user_id} onChange={event => set('assigned_user_id', event.target.value)}><SelectOptions options={options.users} empty="Системные администраторы" /></select></FormField>
      <FormField label="Заявка"><select value={form.ticket_id} onChange={event => set('ticket_id', event.target.value)}><option value="">Без заявки</option>{options.tickets.map(ticket => <option key={ticket.id} value={ticket.id}>{ticket.ticket_number} · {ticket.point}</option>)}</select></FormField>
    </div></form>
  </ModalShell>;
}

function RelationEditor({ item, options, onClose, onSaved }) {
  const [relatedId, setRelatedId] = useState('');
  const [type, setType] = useState('connected');
  const [error, setError] = useState('');
  const save = async () => {
    if (!relatedId) return setError('Выберите оборудование');
    try { await createRelation(item.id, { related_item_id: Number(relatedId), relation_type: type }); onSaved(); }
    catch (requestError) { setError(errorMessage(requestError)); }
  };
  return <ModalShell title="Связать оборудование" subtitle="Например: регистратор контролирует камеры, старое устройство заменено новым." onClose={onClose} footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Отмена</button><button type="button" className={styles.primaryButton} onClick={save}>Создать связь</button></>}>
    {error && <div className={styles.inlineError}>{error}</div>}<div className={styles.compactForm}><FormField label="Тип связи"><select value={type} onChange={event => setType(event.target.value)}><SelectOptions options={options.relation_types} /></select></FormField><FormField label="Оборудование"><select value={relatedId} onChange={event => setRelatedId(event.target.value)}><option value="">Выберите из реестра</option>{options.assets.filter(asset => asset.id !== item.id).map(asset => <option key={asset.id} value={asset.id}>{asset.name} · {asset.point?.name || 'Без точки'}</option>)}</select></FormField></div>
  </ModalShell>;
}

function TicketEditor({ item, options, onClose, onSaved }) {
  const linked = new Set((item.tickets || []).map(ticket => ticket.id));
  const [ticketId, setTicketId] = useState('');
  const [error, setError] = useState('');
  const save = async () => {
    if (!ticketId) return setError('Выберите заявку');
    try { await linkTicket(item.id, Number(ticketId)); onSaved(); }
    catch (requestError) { setError(errorMessage(requestError)); }
  };
  return <ModalShell title="Связать с заявкой" subtitle="Заявка появится в карточке оборудования и в истории работ." onClose={onClose} footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Отмена</button><button type="button" className={styles.primaryButton} onClick={save}>Связать</button></>}>
    {error && <div className={styles.inlineError}>{error}</div>}<div className={styles.compactForm}><FormField label="Заявка Helpdesk"><select value={ticketId} onChange={event => setTicketId(event.target.value)}><option value="">Выберите заявку</option>{options.tickets.filter(ticket => !linked.has(ticket.id)).map(ticket => <option key={ticket.id} value={ticket.id}>{ticket.ticket_number} · {ticket.point} · {ticket.description.slice(0, 80)}</option>)}</select></FormField></div>
  </ModalShell>;
}

function QrDialog({ item, onClose }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let objectUrl = '';
    getItemQrBlob(item.id).then(blob => { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); }).catch(requestError => setError(errorMessage(requestError, 'Не удалось создать QR-код')));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [item.id]);
  return <ModalShell title="QR-метка" subtitle="Распечатайте и наклейте на устройство — код откроет его карточку." onClose={onClose} footer={<><button type="button" className={styles.secondaryButton} onClick={onClose}>Закрыть</button>{url && <a className={styles.primaryButton} href={url} download={`asset-${item.id}-qr.png`}>Скачать PNG</a>}</>}>
    <div className={styles.qrBox}>{error ? <div className={styles.inlineError}>{error}</div> : url ? <><img src={url} alt={`QR-код ${item.name}`} /><strong>{item.name}</strong><span>{item.inventory_number || `Оборудование #${item.id}`}</span></> : <span className="spinner" />}</div>
  </ModalShell>;
}
